import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { Layout } from "@/components/Layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SeoHead } from "@/components/SeoHead";

// Critical path — loaded eagerly (needed for first paint on any route)
import Home from "@/pages/Home";
import Placeholder from "@/pages/Placeholder";

// All other pages — lazy loaded (code-split per route)
const Login           = lazy(() => import("@/pages/Login"));
const Register        = lazy(() => import("@/pages/Register"));
const Profile         = lazy(() => import("@/pages/Profile"));
const PostAd          = lazy(() => import("@/pages/PostAd"));
const ListingDetail   = lazy(() => import("@/pages/ListingDetail"));
const Search          = lazy(() => import("@/pages/Search"));
const Compare         = lazy(() => import("@/pages/Compare"));
const SuppliersSearch = lazy(() => import("@/pages/SuppliersSearch"));
const MyListings      = lazy(() => import("@/pages/MyListings"));
const FleetParc       = lazy(() => import("@/pages/FleetParc"));
const Favorites       = lazy(() => import("@/pages/Favorites"));
const Messages        = lazy(() => import("@/pages/Messages"));
const ProPublic       = lazy(() => import("@/pages/ProPublic"));
const ProDashboard    = lazy(() => import("@/pages/ProDashboard"));
const Admin           = lazy(() => import("@/pages/Admin"));
const AdminLogin      = lazy(() => import("@/pages/AdminLogin"));
const Pricing         = lazy(() => import("@/pages/Pricing"));
const Subscription    = lazy(() => import("@/pages/Subscription"));
const PaymentSuccess  = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel   = lazy(() => import("@/pages/PaymentCancel"));
const Support         = lazy(() => import("@/pages/Support"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));
const CGU             = lazy(() => import("@/pages/CGU"));
const Confidentialite = lazy(() => import("@/pages/Confidentialite"));
const ForgotPassword  = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword   = lazy(() => import("@/pages/ResetPassword"));
const SavedSearches   = lazy(() => import("@/pages/SavedSearches"));
const ContactedCompanies = lazy(() => import("@/pages/ContactedCompanies"));
const ShopSettings    = lazy(() => import("@/pages/ShopSettings"));
const Invoices        = lazy(() => import("@/pages/Invoices"));
const AccountSettings = lazy(() => import("@/pages/AccountSettings"));
const VerifyEmail     = lazy(() => import("@/pages/VerifyEmail"));
const CheckEmail      = lazy(() => import("@/pages/CheckEmail"));
const SupplierSupport = lazy(() => import("@/pages/SupplierSupport"));
const PendingReviews  = lazy(() => import("@/pages/PendingReviews"));

/** Default SEO per public route; pages can override with their own <SeoHead />. */
const ROUTE_SEO = {
  "/": {
    title: "MATPASS · La plateforme belge du matériel professionnel d'occasion",
    description:
      "Achetez et vendez du matériel professionnel d'occasion partout en Belgique : espaces verts, BTP, agriculture et outillage.",
  },
  "/recherche": {
    title: "Recherche d'annonces",
    description: "Parcourez les annonces de matériel professionnel d'occasion en Belgique.",
  },
  "/comparer": {
    title: "Comparaison d'annonces",
    description: "Comparez côte à côte le prix, la marque, l'état et la localisation de plusieurs annonces MATPASS.",
  },
  "/annonces": {
    title: "Annonces matériel professionnel",
    description: "Toutes les annonces MATPASS : engins, outillage, agriculture, espaces verts.",
  },
  "/fournisseurs": {
    title: "Fournisseurs près de chez vous",
    description: "Trouvez un fournisseur de matériel professionnel en Belgique.",
  },
  "/tarifs": {
    title: "Tarifs Pro & Premium",
    description: "Abonnements MATPASS pour les professionnels : boutique, stats, priorité.",
  },
  "/beta": {
    title: "Beta MATPASS",
    description: "Découvrez la beta MATPASS, la marketplace belge du matériel professionnel.",
  },
  "/mentions-legales": { title: "Mentions légales", description: "Mentions légales MATPASS." },
  "/cgu": { title: "Conditions générales d'utilisation", description: "CGU de la plateforme MATPASS." },
  "/confidentialite": {
    title: "Politique de confidentialité",
    description: "Politique de confidentialité et protection des données MATPASS.",
  },
};

const NOINDEX_PREFIXES = [
  "/admin",
  "/messages",
  "/messagerie",
  "/parametres",
  "/connexion",
  "/inscription",
  "/profil",
  "/mes-annonces",
  "/mon-parc",
  "/mon-catalogue",
  "/favoris",
  "/factures",
  "/abonnement",
  "/paiement",
  "/ma-boutique",
  "/pro-dashboard",
  "/mot-de-passe-oublie",
  "/reinitialiser-mot-de-passe",
  "/verifier-email",
  "/confirmation-email",
  "/mes-recherches",
  "/entreprises-contactees",
  "/support",
  "/deposer",
  "/modifier-annonce",
];

function RouteSeo() {
  const { pathname } = useLocation();
  const exact = ROUTE_SEO[pathname];
  const noindex = NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  // Listing / shop pages set their own SeoHead — skip default to avoid flicker overwrite
  if (pathname.startsWith("/annonce/") || pathname.startsWith("/pro/")) return null;
  return (
    <SeoHead
      title={exact?.title}
      description={exact?.description}
      path={pathname}
      noindex={noindex}
    />
  );
}

/** Legacy email links: /messagerie?c=ID or /messagerie/ID → /messages/ID */
function MessagerieRedirect() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const convId = id || params.get("c");
  return <Navigate to={convId ? `/messages/${convId}` : "/messages"} replace />;
}

/** Reset window scroll on path change only — not on query updates (filters/radius). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={`${location.pathname}${location.search}`}>
      <ScrollToTop />
      <RouteSeo />
      <Layout>
        <Suspense fallback={<div className="matpro-container py-16 text-matpro-anthracite-muted text-sm animate-pulse">…</div>}>
        <Routes>
          <Route path="/beta" element={<Navigate to="/" replace />} />
          <Route path="/" element={<Home />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/inscription" element={<Register />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route path="/profil" element={<Profile />} />
          <Route path="/parametres" element={<AccountSettings />} />
          <Route path="/verifier-email" element={<VerifyEmail />} />
          <Route path="/confirmation-email" element={<CheckEmail />} />
          <Route path="/mes-recherches" element={<SavedSearches />} />
          <Route path="/entreprises-contactees" element={<ContactedCompanies />} />
          <Route path="/recherche" element={<Search />} />
          <Route path="/annonces" element={<Search />} />
          <Route path="/comparer" element={<Compare />} />
          <Route path="/fournisseurs" element={<SuppliersSearch />} />
          <Route path="/deposer" element={<PostAd />} />
          <Route path="/modifier-annonce/:id" element={<PostAd />} />
          <Route path="/annonce/:id" element={<ListingDetail />} />
          <Route path="/mes-annonces" element={<MyListings />} />
          <Route path="/mon-parc" element={<FleetParc />} />
          <Route path="/mon-catalogue" element={<FleetParc />} />
          <Route path="/parc" element={<FleetParc />} />
          <Route path="/parc-machines" element={<FleetParc />} />
          <Route path="/fleet" element={<FleetParc />} />
          <Route path="/favoris" element={<Favorites />} />
          <Route path="/mes-avis" element={<PendingReviews />} />
          {/* Optional :id keeps the same Messages instance (no remount on open/back). */}
          <Route path="/messages/:id?" element={<Messages />} />
          {/* Legacy email deep-links */}
          <Route path="/messagerie" element={<MessagerieRedirect />} />
          <Route path="/messagerie/:id" element={<MessagerieRedirect />} />
          <Route path="/pro/:id" element={<ProPublic />} />
          <Route path="/pro-dashboard" element={<ProDashboard />} />
          <Route path="/ma-boutique" element={<ShopSettings />} />
          <Route path="/factures" element={<Invoices />} />
          <Route path="/admin/connexion" element={<AdminLogin />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/tarifs" element={<Pricing />} />
          <Route path="/abonnement" element={<Subscription />} />
          <Route path="/paiement/success" element={<PaymentSuccess />} />
          <Route path="/paiement/annule" element={<PaymentCancel />} />
          <Route path="/support" element={<Support />} />
          <Route path="/support-matpass" element={<SupplierSupport />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/pro" element={<Navigate to="/pro-dashboard" replace />} />
          <Route path="*" element={<Placeholder />} />
        </Routes>
        </Suspense>
      </Layout>
      <Toaster position="top-right" richColors />
    </ErrorBoundary>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <AppShell />
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
